import React, { useEffect, useRef, useState } from "react";
import { api, fmtDateTime } from "../lib/api";
import { Bell, Check, AlarmClock } from "lucide-react";
import { useWS } from "../lib/ws";

const REMINDER_SOUND = "data:audio/wav;base64,UklGRngDAABXQVZFZm10IBAAAAABAAEAgD4AAIA+AAABAAgAZGF0YVQDAACCgoKBgICAgICAgICAgICAgICAgICAgICAgICAgIGCg4SFhoeHiIiJiYmKiouLjIyNjY2Ojo+Pj5CQkJGRkZKTk5OUlJWVlZaXl5iZmZqam5ydnZ6en5+goKGio6Ojo6Oio6KioqGhoKCgnp6dnZubmpqZmJiXl5aVlJSTko+OjoyLi4qJh4WFg4J/fn18e3p4eHd1c3NycW9ubGxraWhnZmVlZGRkZGRkZGRlZmdpamttbnBycnN1dnh6fH+BgoWGiIqMjo+RkpSWl5manJ6foaKjpqanqaqqq6yrrKytra2trq6urq6urq6uqqqrqqqpqaimpKSjoqCfnZuam5qXl5aVlJKRkI+Ojo2MjIuLi4qKiomJiIiIiIiIh4iIiIiIiYmJiomKiYqKiYqKi4uMjIyNjY6Oj4+QkZKSk5SVlpaXmJmampucnJ2en6CgoaKio6SkpaWmpqenp6ioqKmpqaqqqqurq6urq6urqqqqqqmpqaioqKioqKinp6enp6ioqKiop6anp6enqKipqaqqqqqqqqqqqampqampqampqampqampqamrq6urq6urq6ur";

export default function TaskReminder() {
  const [active, setActive] = useState(null);
  const [dismissed, setDismissed] = useState(() => new Set());
  const audioRef = useRef(null);
  const stopAtRef = useRef(null);
  const { subscribe } = useWS();

  const trigger = (list) => {
    const t = (list || []).find(x => !dismissed.has(x.id));
    if (t && !active) {
      setActive(t);
      stopAtRef.current = Date.now() + 2 * 60 * 1000;
      loopSound();
    }
  };

  const poll = async () => {
    try {
      const r = await api.get("/tasks/reminders/due");
      trigger(r.data || []);
    } catch {}
  };

  const loopSound = () => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch(()=>{});
  };

  useEffect(() => {
    poll();
    // WS push for instant task reminders
    const unsub = subscribe("task_due", (list) => trigger(list));
    const id = setInterval(poll, 60000);
    return () => { clearInterval(id); unsub(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, dismissed, subscribe]);

  // Handle audio looping up to 2 min
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onEnd = () => {
      if (active && stopAtRef.current && Date.now() < stopAtRef.current) {
        el.currentTime = 0;
        el.play().catch(()=>{});
      }
    };
    el.addEventListener("ended", onEnd);
    return () => el.removeEventListener("ended", onEnd);
  }, [active]);

  const stopSound = () => {
    try { audioRef.current?.pause(); audioRef.current.currentTime = 0; } catch {}
    stopAtRef.current = null;
  };

  const snooze = async () => {
    if (!active) return;
    await api.post(`/tasks/${active.id}/snooze`, { minutes: 5 });
    stopSound();
    setDismissed(prev => { const n = new Set(prev); n.add(active.id); return n; });
    setActive(null);
  };

  const complete = async () => {
    if (!active) return;
    await api.post(`/tasks/${active.id}/complete`);
    stopSound();
    setDismissed(prev => { const n = new Set(prev); n.add(active.id); return n; });
    setActive(null);
  };

  return (
    <>
      <audio ref={audioRef} src={REMINDER_SOUND} preload="auto"/>
      {active && (
        <div data-testid="task-reminder-popup" className="fixed top-4 right-4 z-[60] w-80 bg-white rounded-xl border-2 border-[#4f87ff] shadow-2xl overflow-hidden fade-in">
          <div className="bg-[#4f87ff] text-white px-4 py-2.5 flex items-center gap-2">
            <AlarmClock size={18} className="reminder-ring"/>
            <div className="font-medium" style={{fontFamily:'Outfit'}}>Task Reminder</div>
          </div>
          <div className="p-4">
            <div className="font-semibold text-gray-900 mb-1">{active.title}</div>
            {active.description && <div className="text-xs text-gray-600 mb-2">{active.description}</div>}
            <div className="text-xs text-gray-500 mb-4">Due: {fmtDateTime(active.due_at)}</div>
            <div className="flex gap-2">
              <button data-testid="task-snooze" onClick={snooze} className="flex-1 px-3 py-2 rounded-md border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50">Snooze 5 min</button>
              <button data-testid="task-complete" onClick={complete} className="flex-1 px-3 py-2 rounded-md bg-[#4f87ff] hover:bg-[#3a6fe0] text-white text-sm font-medium flex items-center justify-center gap-1"><Check size={14}/> Complete</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
